import { z } from 'zod';

/**
 * The notification catalogue — WHAT the product can tell a customer, and WHICH pieces of the
 * shop's own data a message may be built from.
 *
 * Two closed sets live here, and they are closed for the same reason the stage keys are:
 *
 *  - **Events.** The CRM decides when an event happens (task 15 wires stage changes, payments
 *    and deliveries to them); the owner decides which of their own WhatsApp templates each
 *    event uses. If a shop could invent an event, nothing in the product could ever fire it.
 *  - **Variables.** A template says "Hello {{1}}, order {{2}} is ready". Binding those numbers
 *    to shop data has to be picked from a documented list, or every mapping becomes a
 *    free-text field that silently renders blank the first time somebody typos it.
 *
 * Nothing in this file mentions the messaging provider. An event is a product concept; how it
 * reaches a phone is the adapter's business.
 */

// --- The variables a template may be bound to --------------------------------------------------

export const NOTIFICATION_VARIABLE_KEYS = [
  'shop.name',
  'shop.phone',
  'customer.name',
  'customer.phone',
  'order.number',
  'order.itemCount',
  'order.garments',
  'order.promisedDate',
  'order.totalAmount',
  'order.paidAmount',
  'order.balanceAmount',
  'payment.amount',
  'payment.method',
  'payment.date',
  // Appointments (task 14): the booking reminder. What it says beyond who it is for and where —
  // when, and who the customer is seeing.
  'appointment.date',
  'appointment.time',
  'appointment.purpose',
  'appointment.staffName',
  // Occasions (birthday and anniversary greetings). The date is the only thing a greeting
  // knows beyond who it is for — "many happy returns" needs nothing else, and a template that
  // wants to name the day should be able to.
  'occasion.date',
  'plan.name',
  'trial.endsOn',
  'subscription.amount',
  'subscription.renewsOn',
  // Stock (task 27). A low-stock notice names WHAT is low rather than linking to a screen —
  // the owner reading it on WhatsApp is usually not at the shop and cannot open the app.
  'stock.items',
  'stock.count',
  // Alterations: a garment brought back, or a walk-in. Their own series and their own balance,
  // so they bind their own fields rather than borrowing an order's.
  'alteration.number',
  'alteration.garment',
  'alteration.promisedDate',
  'alteration.balanceAmount',
] as const;

export type NotificationVariableKey = (typeof NOTIFICATION_VARIABLE_KEYS)[number];

export const notificationVariableKeySchema = z.enum(NOTIFICATION_VARIABLE_KEYS);

export interface NotificationVariableDefinition {
  readonly key: NotificationVariableKey;
  /** What the owner sees in the binding dropdown. */
  readonly label: string;
  /** Rendered in the preview and in a test send, so a mapping can be checked before it is live. */
  readonly sample: string;
}

export const NOTIFICATION_VARIABLES: Readonly<
  Record<NotificationVariableKey, NotificationVariableDefinition>
> = Object.freeze({
  'shop.name': { key: 'shop.name', label: 'Shop name', sample: 'Asha Boutique' },
  'shop.phone': { key: 'shop.phone', label: 'Shop phone', sample: '9876543210' },
  'customer.name': { key: 'customer.name', label: 'Customer name', sample: 'Lakshmi' },
  'customer.phone': { key: 'customer.phone', label: 'Customer phone', sample: '9812345678' },
  'order.number': { key: 'order.number', label: 'Order number', sample: 'ORD-2026-0042' },
  'order.itemCount': { key: 'order.itemCount', label: 'Number of garments', sample: '3' },
  'order.garments': { key: 'order.garments', label: 'Garments', sample: '2 blouses, 1 lehenga' },
  'order.promisedDate': {
    key: 'order.promisedDate',
    label: 'Promised date',
    sample: '20 Aug 2026',
  },
  'order.totalAmount': { key: 'order.totalAmount', label: 'Order total', sample: '₹4,500' },
  'order.paidAmount': { key: 'order.paidAmount', label: 'Amount paid', sample: '₹2,000' },
  'order.balanceAmount': { key: 'order.balanceAmount', label: 'Balance due', sample: '₹2,500' },
  'payment.amount': { key: 'payment.amount', label: 'Payment amount', sample: '₹2,000' },
  'payment.method': { key: 'payment.method', label: 'Payment method', sample: 'UPI' },
  'payment.date': { key: 'payment.date', label: 'Payment date', sample: '12 Aug 2026' },
  'appointment.date': { key: 'appointment.date', label: 'Appointment date', sample: '20 Aug 2026' },
  'appointment.time': { key: 'appointment.time', label: 'Appointment time', sample: '4:00 pm' },
  'appointment.purpose': {
    key: 'appointment.purpose',
    label: 'Appointment purpose',
    sample: 'Trial fitting',
  },
  'occasion.date': { key: 'occasion.date', label: 'Occasion date', sample: '15 Mar' },
  'appointment.staffName': {
    key: 'appointment.staffName',
    label: 'Staff member',
    sample: 'Priya Nair',
  },
  'plan.name': { key: 'plan.name', label: 'Subscription plan', sample: 'Growth' },
  'trial.endsOn': { key: 'trial.endsOn', label: 'Trial end date', sample: '26 Aug 2026' },
  'subscription.amount': {
    key: 'subscription.amount',
    label: 'Subscription amount',
    sample: '₹999',
  },
  'stock.items': {
    key: 'stock.items',
    label: 'What is low',
    sample: 'Kanjeevaram silk (2.5 m), Gold zari lace (1 m)',
  },
  'stock.count': { key: 'stock.count', label: 'How many items are low', sample: '2' },
  'subscription.renewsOn': {
    key: 'subscription.renewsOn',
    label: 'Renewal date',
    sample: '1 Sep 2026',
  },
  'alteration.number': {
    key: 'alteration.number',
    label: 'Alteration number',
    sample: 'ALT/2026-27/0007',
  },
  'alteration.garment': { key: 'alteration.garment', label: 'Garment', sample: 'Blouse' },
  'alteration.promisedDate': {
    key: 'alteration.promisedDate',
    label: 'Alteration promised date',
    sample: '22 Aug 2026',
  },
  'alteration.balanceAmount': {
    key: 'alteration.balanceAmount',
    label: 'Alteration balance due',
    sample: '₹300',
  },
});

/** Sample values keyed by variable, for the preview and the owner's test send. */
export function sampleVariableValues(): Record<NotificationVariableKey, string> {
  const out = {} as Record<NotificationVariableKey, string>;
  for (const key of NOTIFICATION_VARIABLE_KEYS) out[key] = NOTIFICATION_VARIABLES[key].sample;
  return out;
}

// --- The events ---------------------------------------------------------------------------------

/**
 * What a message is ABOUT. It decides the entity a send is deduplicated against: two payments
 * on one order are two `payment_received` messages, but "your order is ready" is one message
 * for that order however many times the board is nudged.
 */
export const NOTIFICATION_SUBJECTS = [
  'order',
  'payment',
  'subscription',
  'stock',
  'appointment',
  'customer',
  'alteration',
] as const;
export const notificationSubjectSchema = z.enum(NOTIFICATION_SUBJECTS);
export type NotificationSubject = (typeof NOTIFICATION_SUBJECTS)[number];

/** Who the message is for. Owner notices go to the shop's own number, not to a customer. */
export const NOTIFICATION_AUDIENCES = ['customer', 'owner'] as const;
export type NotificationAudience = (typeof NOTIFICATION_AUDIENCES)[number];

export const NOTIFICATION_EVENTS = [
  'order_booked',
  'order_ready',
  'delivery_reminder',
  'order_delivered',
  'balance_due',
  'payment_received',
  'appointment_scheduled',
  'customer_birthday',
  'customer_anniversary',
  'trial_ending',
  'subscription_payment_failed',
  'low_stock',
  'alteration_booked',
  'alteration_ready',
] as const;

export type NotificationEvent = (typeof NOTIFICATION_EVENTS)[number];

export const notificationEventSchema = z.enum(NOTIFICATION_EVENTS);

export interface NotificationEventDefinition {
  readonly key: NotificationEvent;
  readonly defaultLabel: string;
  /** One sentence, shown under the event name on the mapping screen. */
  readonly description: string;
  readonly audience: NotificationAudience;
  readonly subject: NotificationSubject;
  /** The only variables this event can bind — a payment field on "order booked" is nonsense. */
  readonly variables: readonly NotificationVariableKey[];
  /** Whether a shop that has just connected WhatsApp starts with this event switched on. */
  readonly defaultEnabled: boolean;
}

const APPOINTMENT_VARIABLES: readonly NotificationVariableKey[] = [
  'shop.name',
  'shop.phone',
  'customer.name',
  'customer.phone',
  'appointment.date',
  'appointment.time',
  'appointment.purpose',
  'appointment.staffName',
];

/**
 * A greeting knows the least of any message this product sends: who it is for, which shop is
 * sending it, and which day it is about. Deliberately no order fields — a birthday wish that
 * mentions an outstanding balance is a debt collection letter with a cake on it.
 */
const OCCASION_VARIABLES: readonly NotificationVariableKey[] = [
  'shop.name',
  'shop.phone',
  'customer.name',
  'customer.phone',
  'occasion.date',
];

const ORDER_VARIABLES: readonly NotificationVariableKey[] = [
  'shop.name',
  'shop.phone',
  'customer.name',
  'customer.phone',
  'order.number',
  'order.itemCount',
  'order.garments',
  'order.promisedDate',
  'order.totalAmount',
  'order.paidAmount',
  'order.balanceAmount',
];

const ALTERATION_VARIABLES: readonly NotificationVariableKey[] = [
  'shop.name',
  'shop.phone',
  'customer.name',
  'customer.phone',
  'alteration.number',
  'alteration.garment',
  'alteration.promisedDate',
  'alteration.balanceAmount',
];

export const NOTIFICATION_EVENT_DEFINITIONS: Readonly<
  Record<NotificationEvent, NotificationEventDefinition>
> = Object.freeze({
  order_booked: {
    key: 'order_booked',
    defaultLabel: 'Order booked',
    description: 'Sent when an order is taken, with the promised date.',
    audience: 'customer',
    subject: 'order',
    variables: ORDER_VARIABLES,
    defaultEnabled: true,
  },
  order_ready: {
    key: 'order_ready',
    defaultLabel: 'Order ready',
    description: 'Sent when every garment on the order is ready for collection.',
    audience: 'customer',
    subject: 'order',
    variables: ORDER_VARIABLES,
    defaultEnabled: true,
  },
  delivery_reminder: {
    key: 'delivery_reminder',
    defaultLabel: 'Collection reminder',
    description: 'A nudge for an order that has been ready for a while and not collected.',
    audience: 'customer',
    subject: 'order',
    variables: ORDER_VARIABLES,
    defaultEnabled: false,
  },
  order_delivered: {
    key: 'order_delivered',
    defaultLabel: 'Order delivered',
    description: 'A thank-you once the order has been handed over.',
    audience: 'customer',
    subject: 'order',
    variables: ORDER_VARIABLES,
    // Off by default. A thank-you is a nicety and every send costs the shop a conversation;
    // the two events a shop actually signs up for are "booked" and "ready".
    defaultEnabled: false,
  },
  balance_due: {
    key: 'balance_due',
    defaultLabel: 'Balance due',
    description: 'Reminds the customer what is still owed on a delivered order.',
    audience: 'customer',
    subject: 'order',
    variables: ORDER_VARIABLES,
    defaultEnabled: false,
  },
  payment_received: {
    key: 'payment_received',
    defaultLabel: 'Payment received',
    description: 'A receipt for a payment, with the balance that remains.',
    audience: 'customer',
    subject: 'payment',
    variables: [...ORDER_VARIABLES, 'payment.amount', 'payment.method', 'payment.date'],
    defaultEnabled: false,
  },
  appointment_scheduled: {
    key: 'appointment_scheduled',
    defaultLabel: 'Appointment booked',
    description:
      'Sent when a fitting or measurement appointment is booked, with the date, time and who ' +
      'they are seeing.',
    audience: 'customer',
    subject: 'appointment',
    variables: APPOINTMENT_VARIABLES,
    // Off by default, like every other customer notice save the two the whole feature is sold
    // on: a shop must opt in before this starts messaging anybody.
    defaultEnabled: false,
  },
  customer_birthday: {
    key: 'customer_birthday',
    defaultLabel: 'Birthday greeting',
    description: 'Wishes a customer a happy birthday on the day, if they gave you the date.',
    audience: 'customer',
    subject: 'customer',
    variables: OCCASION_VARIABLES,
    // Off by default, and emphatically so. A shop that connects WhatsApp has not thereby asked
    // to message everybody whose birthday it happens to know.
    defaultEnabled: false,
  },
  customer_anniversary: {
    key: 'customer_anniversary',
    defaultLabel: 'Anniversary greeting',
    description: 'Wishes a customer on the anniversary they gave you, on the day.',
    audience: 'customer',
    subject: 'customer',
    variables: OCCASION_VARIABLES,
    defaultEnabled: false,
  },
  trial_ending: {
    key: 'trial_ending',
    defaultLabel: 'Trial ending',
    description: 'Tells the shop owner their free trial is about to end.',
    audience: 'owner',
    subject: 'subscription',
    variables: ['shop.name', 'plan.name', 'trial.endsOn', 'subscription.amount'],
    defaultEnabled: false,
  },
  subscription_payment_failed: {
    key: 'subscription_payment_failed',
    defaultLabel: 'Subscription payment failed',
    description: 'Tells the shop owner a subscription charge did not go through.',
    audience: 'owner',
    subject: 'subscription',
    variables: ['shop.name', 'plan.name', 'subscription.amount', 'subscription.renewsOn'],
    defaultEnabled: false,
  },
  low_stock: {
    key: 'low_stock',
    defaultLabel: 'Low stock',
    description: 'Tells the shop what has fallen to its reorder level, so it can buy more.',
    audience: 'owner',
    subject: 'stock',
    variables: ['shop.name', 'stock.items', 'stock.count'],
    // On by default for a shop that has connected WhatsApp AND uses stock: unlike the customer
    // notices, this one costs the shop nothing it did not already want — running out of the
    // fabric a bride chose is the expensive outcome it prevents.
    defaultEnabled: true,
  },
  /**
   * The two alteration notices are sent by a person, from the alteration — nothing fires them on
   * its own. The booked one is the customer's slip for a garment they left behind, which matters
   * most for a walk-in, who has no order receipt to show at collection.
   */
  alteration_booked: {
    key: 'alteration_booked',
    defaultLabel: 'Alteration booked',
    description: 'Confirms a garment left for alteration, with its number and promised date.',
    audience: 'customer',
    subject: 'alteration',
    variables: ALTERATION_VARIABLES,
    defaultEnabled: false,
  },
  alteration_ready: {
    key: 'alteration_ready',
    defaultLabel: 'Alteration ready',
    description: 'Tells the customer an altered garment is ready to collect, with any balance.',
    audience: 'customer',
    subject: 'alteration',
    variables: ALTERATION_VARIABLES,
    defaultEnabled: false,
  },
});

export function isNotificationEvent(value: unknown): value is NotificationEvent {
  return typeof value === 'string' && (NOTIFICATION_EVENTS as readonly string[]).includes(value);
}

export function notificationEventDefinition(event: NotificationEvent): NotificationEventDefinition {
  return NOTIFICATION_EVENT_DEFINITIONS[event];
}

/** True when this event may bind that variable. The mapping endpoint refuses anything else. */
export function eventAllowsVariable(
  event: NotificationEvent,
  variable: NotificationVariableKey,
): boolean {
  return NOTIFICATION_EVENT_DEFINITIONS[event].variables.includes(variable);
}

/** Events that reach a customer's phone, as opposed to the owner's own. */
export const CUSTOMER_NOTIFICATION_EVENTS: readonly NotificationEvent[] = Object.freeze(
  NOTIFICATION_EVENTS.filter((key) => NOTIFICATION_EVENT_DEFINITIONS[key].audience === 'customer'),
);

// --- Rendering ------------------------------------------------------------------------------------

/**
 * Positional placeholders, `{{1}}`-style, are what WhatsApp's own template format uses and what
 * every provider on top of it exposes — so this is a message-template convention rather than a
 * provider detail, and both sides render a preview with the same function.
 */
const PLACEHOLDER_PATTERN = /\{\{\s*(\d+)\s*\}\}/g;

/** The placeholder numbers a template body uses, ascending and deduplicated. */
export function templatePlaceholders(body: string): number[] {
  const found = new Set<number>();
  for (const match of body.matchAll(PLACEHOLDER_PATTERN)) {
    const position = Number(match[1]);
    if (Number.isInteger(position) && position > 0) found.add(position);
  }
  return [...found].sort((a, b) => a - b);
}

/**
 * Substitute values into a template body. An unbound placeholder renders as `—` rather than
 * being left as `{{2}}`: the owner reading the preview should see the hole where a customer
 * would see one, and neither should ever see the raw syntax.
 */
export function renderTemplateBody(body: string, values: Record<string, string>): string {
  return body.replace(PLACEHOLDER_PATTERN, (_match, position: string) => {
    const value = values[position];
    return value === undefined || value === '' ? '—' : value;
  });
}

// --- The message a shop sends from its own phone -------------------------------------------------

/**
 * What each event says when there is no approved template to say it — the wording used by the
 * "open WhatsApp with this typed out" fallback (task 15).
 *
 * A shop that has not connected Relay still gets the product's value on day one: staff tap once
 * and their own WhatsApp opens with the message written. That is also the honest moment to ask
 * them to connect, because they have just seen what connecting automates.
 *
 * Written as LINES, and a line whose fields are not all available is dropped whole. That is the
 * cheap way to avoid "Expected on ." on an order with no promised date: the sentence either has
 * everything it needs or it does not appear.
 */
const FALLBACK_MESSAGE_LINES: Readonly<Record<NotificationEvent, readonly string[]>> =
  Object.freeze({
    order_booked: [
      'Hello {customer.name},',
      'We have booked your order {order.number} at {shop.name}.',
      'Garments: {order.garments}.',
      'Expected by {order.promisedDate}.',
    ],
    order_ready: [
      'Hello {customer.name},',
      'Your order {order.number} is ready for collection at {shop.name}.',
      'Balance due: {order.balanceAmount}.',
    ],
    delivery_reminder: [
      'Hello {customer.name},',
      'A reminder from {shop.name} about your order {order.number}, due {order.promisedDate}.',
    ],
    order_delivered: [
      'Hello {customer.name},',
      'Thank you for collecting order {order.number} from {shop.name}. We hope you love it!',
    ],
    balance_due: [
      'Hello {customer.name},',
      'A gentle reminder from {shop.name}: {order.balanceAmount} is outstanding on order {order.number}.',
    ],
    payment_received: [
      'Hello {customer.name},',
      'We have received {payment.amount} towards order {order.number} at {shop.name}.',
      'Balance due: {order.balanceAmount}.',
    ],
    appointment_scheduled: [
      'Hello {customer.name},',
      'Your appointment at {shop.name} is booked for {appointment.date} at {appointment.time}.',
      'Purpose: {appointment.purpose}.',
      'You will be seeing {appointment.staffName}.',
    ],
    customer_birthday: [
      'Happy birthday, {customer.name}!',
      'With warm wishes from all of us at {shop.name}.',
    ],
    customer_anniversary: [
      'Happy anniversary, {customer.name}!',
      'With warm wishes from all of us at {shop.name}.',
    ],
    trial_ending: ['Your {shop.name} trial ends on {trial.endsOn}.'],
    low_stock: [
      '{shop.name} — stock running low.',
      '{stock.count} item(s) are at or below their reorder level:',
      '{stock.items}',
    ],
    subscription_payment_failed: [
      'The subscription payment for {shop.name} did not go through.',
      'Amount: {subscription.amount}.',
    ],
    alteration_booked: [
      'Hello {customer.name},',
      'We have taken your {alteration.garment} for alteration at {shop.name}.',
      'Your alteration number is {alteration.number}.',
      'Expected by {alteration.promisedDate}.',
    ],
    alteration_ready: [
      'Hello {customer.name},',
      'Your {alteration.garment} ({alteration.number}) is altered and ready to collect at {shop.name}.',
      'Balance due: {alteration.balanceAmount}.',
    ],
  });

const FIELD_TOKEN_PATTERN = /\{([a-z]+\.[a-zA-Z]+)\}/g;

/**
 * The fallback message for an event, built from the same documented field set a template binds
 * to — so what a shop types by hand and what Relay sends automatically say the same thing.
 */
export function renderFallbackMessage(
  event: NotificationEvent,
  values: Partial<Record<NotificationVariableKey, string>>,
): string {
  const lines: string[] = [];

  for (const line of FALLBACK_MESSAGE_LINES[event]) {
    const tokens = [...line.matchAll(FIELD_TOKEN_PATTERN)].map((match) => match[1] ?? '');
    const complete = tokens.every((token) => {
      const value = values[token as NotificationVariableKey];
      return value !== undefined && value !== '';
    });
    if (!complete) continue;
    lines.push(
      line.replace(
        FIELD_TOKEN_PATTERN,
        (_match, token: string) => values[token as NotificationVariableKey] ?? '',
      ),
    );
  }

  return lines.join('\n');
}

/** India's country code. Customer numbers are stored as ten digits; `wa.me` wants them dialled. */
const DEFAULT_COUNTRY_CODE = '91';

/**
 * A `wa.me` link that opens WhatsApp with the message already typed, on whichever device the
 * staff member is holding.
 *
 * This is the shop's own phone sending as itself — no provider, no API key, no template
 * approval — which is exactly why it is the fallback: the value arrives before the plumbing
 * does. It is deliberately NOT a substitute for Relay at volume, because a person has to press
 * send every time.
 */
export function whatsappDeepLink(
  phone: string,
  text: string,
  countryCode: string = DEFAULT_COUNTRY_CODE,
): string {
  const digits = phone.replace(/\D/g, '');
  const dialled = digits.length > 10 ? digits : `${countryCode}${digits}`;
  return `https://wa.me/${dialled}?text=${encodeURIComponent(text)}`;
}
